import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../store/AuthContext';
import { useLanguage, Lang } from '../store/LanguageContext';
import { Home, Lightbulb, Ruler, FileText, Settings, User, Menu, X, ChevronDown, LogOut, BarChart2, Tag, Mail, Calculator, Shield } from 'lucide-react';
import LoginModal from './LoginModal';
import { adminGetTestAccounts, adminImpersonate, setSessionToken, API_URL, type TestAccount } from '../lib/api';

const NAV_ICONS: Record<string, React.ElementType> = {
  '/': Home,
  '/inspiratie': Lightbulb,
  '/professioneel': Ruler,
  '/restpartijen': Tag,
  '/contact': Mail,
  '/offerte': FileText,
  '/calculator': Calculator,
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchAccounts, setSwitchAccounts] = useState<TestAccount[]>([]);
  const [switching, setSwitching] = useState<string | null>(null);
  const switchRef = useRef<HTMLDivElement>(null);
  const { user, logout, setUser } = useAuth();
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();
  const menuRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (switchRef.current && !switchRef.current.contains(e.target as Node)) {
        setSwitchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // MOET vóór de effects staan (TDZ-crash 19/8): wordt in een deps-array
  // gebruikt en crashte de hele bundel toen hij lager gedeclareerd stond.
  const isImpersonating = !!localStorage.getItem('gvs-admin-token');

  // Load test-account list when superadmin/owner opens dropdown
  const canSwitch = user?.role === 'superadmin' || user?.role === 'owner';
  useEffect(() => {
    if ((canSwitch || isImpersonating) && (switchOpen || open) && switchAccounts.length === 0) {
      // Wens Franky (19/8): vaste volgorde — Filip (hoofd-eigenaar) eerst,
      // dan Bicek en andere eigenaars, dan resellers, dan klanten.
      const laad = isImpersonating
        ? fetch(`${API_URL}/api/admin/test-accounts`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('gvs-admin-token')}` },
          }).then(r => r.json()).then(j => (j.ok ? j.data : []) as TestAccount[])
        : adminGetTestAccounts();
      void laad.then(lijst => {
        const rolOrde: Record<string, number> = { superadmin: 0, owner: 1, reseller: 2, customer: 3 };
        const typeOrde: Record<string, number> = { groothandel: 0, kleinhandel: 1, bouwonderneming: 2, installateur: 3 };
        setSwitchAccounts([...lijst].sort((a, b) => {
          const ra = rolOrde[a.role] ?? 9, rb = rolOrde[b.role] ?? 9;
          if (ra !== rb) return ra - rb;
          const aFilip = a.email === 'filip@gevelsteenbedrijf.be' ? 0 : 1;
          const bFilip = b.email === 'filip@gevelsteenbedrijf.be' ? 0 : 1;
          if (aFilip !== bFilip) return aFilip - bFilip;
          // Wens Franky (19/8): handelaars per type samen (groothandel,
          // kleinhandel, bouwonderneming, installateur)
          const ta = typeOrde[a.reseller_type ?? ''] ?? 8, tb = typeOrde[b.reseller_type ?? ''] ?? 8;
          if (ta !== tb) return ta - tb;
          return a.name.localeCompare(b.name, 'nl');
        }));
      }).catch(() => {});
    }
  }, [canSwitch, isImpersonating, switchOpen, open, switchAccounts.length]);

  const switchTo = async (acc: TestAccount) => {
    setSwitching(acc.email);
    try {
      // Save current admin token so the impersonated session can return.
      const currentToken = localStorage.getItem('gevel_session_token');
      if (currentToken && user) {
        localStorage.setItem('gvs-admin-token', currentToken);
        localStorage.setItem('gvs-admin-name', user.name);
        localStorage.setItem('gvs-admin-role', user.role);
      }
      const { token, user: targetUser } = await adminImpersonate(acc.email);
      setSessionToken(token);
      setUser(targetUser);
      const target =
        acc.role === 'superadmin' || acc.role === 'owner' ? '/admin' :
        acc.role === 'reseller' ? '/reseller-dashboard' :
        '/dashboard';
      window.location.href = target;
    } catch (e) {
      alert((e as Error).message);
      setSwitching(null);
    }
  };

  // Wens Franky (19/8, 'simplicity'): vanuit test-modus DIRECT naar een
  // ander account wisselen zonder eerst terug naar Filip. De bewaarde
  // admin-token doet de wissel; de terugweg blijft intact.
  const wisselDirect = async (acc: TestAccount) => {
    try {
      const adminToken = localStorage.getItem('gvs-admin-token');
      const r = await fetch(`${API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ email: acc.email }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Wisselen mislukt');
      setSessionToken(j.data.token);
      const target = acc.role === 'superadmin' || acc.role === 'owner' ? '/admin'
        : acc.role === 'reseller' ? '/reseller-dashboard' : '/dashboard';
      window.location.href = target;
    } catch (e) { alert((e as Error).message); }
  };

  const goBackToAdmin = () => {
    const savedToken = localStorage.getItem('gvs-admin-token');
    if (!savedToken) return;
    setSessionToken(savedToken);
    localStorage.removeItem('gvs-admin-token');
    localStorage.removeItem('gvs-admin-name');
    localStorage.removeItem('gvs-admin-role');
    setUser(null);
    window.location.href = '/admin';
  };


  // Wide nav surface — important for SEO (more crawlable internal links +
  // anchor-text variety) and for users to discover all sections quickly.
  // Order: Home → Inspiratie → Custom → Restpartijen (lokvogel) → Contact.
  const links = [
    { to: '/',              label: t('nav.home') },
    { to: '/inspiratie',    label: t('nav.inspiration') },
    { to: '/professioneel', label: t('nav.custom') },
    { to: '/contact',       label: t('nav.contact') },
  ];

  const langOptions: { code: Lang; label: string; flag: string; full: string }[] = [
    { code: 'nl', label: 'NL', flag: '🇳🇱', full: 'Nederlands' },
    { code: 'fr', label: 'FR', flag: '🇫🇷', full: 'Français' },
    { code: 'en', label: 'EN', flag: '🇬🇧', full: 'English' },
    { code: 'pl', label: 'PL', flag: '🇵🇱', full: 'Polski' },
  ];

  const currentLang = langOptions.find(l => l.code === lang) || langOptions[0];

  return (
    <>
      <nav
        className={`fixed left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm ${
          // v3.2.68 — banner verschijnt enkel nog tijdens impersonation
          // (zie TestAccountSwitcher.tsx ReturnBanner). Owners/superadmins
          // die niet imitateren zien geen banner meer → top-0.
          (typeof localStorage !== 'undefined' && localStorage.getItem('gvs-admin-token'))
            ? 'top-9'
            : 'top-0'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-11 lg:h-[72px]">
            {/* Logo — klik gaat naar de top van de homepage (standaard SPA
                 conventie). v3.2.88 — eerder scrollde het naar #calculator,
                 maar dat voelde 'de verkeerde kant op' aan: vanaf de hero
                 wilde je naar de top zien (logo = home), niet naar beneden
                 naar de calculator. De reset-event blijft zodat de quote-
                 form alsnog wordt opgeschoond. */}
            <Link
              to="/"
              onClick={(e) => {
                // Custom event triggert de calculator om z'n quote-form
                // en quote-error te resetten (luistert in ProductsPage.tsx).
                window.dispatchEvent(new CustomEvent('gvs:reset-calculator'));

                if (location.pathname === '/') {
                  // Al op homepage: scroll naar de TOP van de pagina.
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                // Anders: react-router navigeert naar /, browser landt
                // automatisch boven aan de pagina.
              }}
              className="flex items-center gap-2 flex-shrink-0"
            >
              <div className="w-7 h-7 lg:w-9 lg:h-9 bg-[#c47a3a] rounded-md lg:rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm lg:text-lg">G</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-[#1a1a1a] tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
                  Gevelsteenbedrijf
                </span>
                <span className="text-[10px] block text-[#8a7e74] -mt-0.5 tracking-widest uppercase">Premium Baksteenstrips</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              {links.map(l => (
                <Link key={l.to} to={l.to}
                  className={`px-3.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-200 ${
                    location.pathname === l.to
                      ? 'text-[#c47a3a] bg-[#c47a3a]/5 font-semibold'
                      : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-gray-50'
                  }`}>
                  {l.label}
                </Link>
              ))}
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-1.5 lg:gap-2">
              {/* Language Dropdown */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1 lg:gap-1.5 px-1.5 lg:px-2.5 py-1 lg:py-1.5 rounded-md lg:rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-xs lg:text-sm"
                >
                  <span className="font-medium text-[#4a4a4a]">{currentLang.flag}</span>
                  <span className="hidden lg:inline font-medium text-[#4a4a4a]">{currentLang.label}</span>
                  <ChevronDown size={10} className={`text-[#8a7e74] transition-transform lg:w-3 lg:h-3 ${langOpen ? 'rotate-180' : ''}`} />
                </button>

                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 lg:mt-1.5 bg-white rounded-lg shadow-lg border border-gray-100 py-1 min-w-[140px] lg:min-w-[160px] z-50">
                    {langOptions.map(opt => (
                      <button
                        key={opt.code}
                        onClick={() => { setLang(opt.code); setLangOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          lang === opt.code
                            ? 'bg-[#c47a3a]/5 text-[#c47a3a] font-semibold'
                            : 'text-[#4a4a4a] hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-base">{opt.flag}</span>
                        <span>{opt.full}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Logged-in user — dashboard-link gebaseerd op rol.
                   v3.2.64: owners/superadmins krijgen nu ook een expliciete
                   'Admin'-link (vroeger alleen via auto-redirect bij login,
                   maar dat werkt niet als Filip naar de homepage navigeert
                   en terug wil). Resellers/klanten kregen al hun eigen link. */}
              {user && (user.role === 'owner' || user.role === 'superadmin') && (
                <Link
                  to="/admin"
                  className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/admin'
                      ? 'text-[#c47a3a] bg-[#c47a3a]/5 font-semibold'
                      : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-gray-50'
                  }`}
                  title="Naar het admin-dashboard"
                >
                  <BarChart2 size={15} />
                  {/* Wens Franky (19/8): toon de voornaam van de admin i.p.v. 'Admin' */}
                  <span>{(user?.name?.split(' ')[0]) || 'Admin'}</span>
                </Link>
              )}
              {user && (user.role === 'reseller' || user.role === 'customer') && (() => {
                const isReseller = user.role === 'reseller';
                const target = isReseller ? '/reseller-dashboard' : '/dashboard';
                const label = isReseller ? 'Reseller' : user.name;
                return (
                  <Link
                    to={target}
                    className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === target
                        ? 'text-[#c47a3a] bg-[#c47a3a]/5 font-semibold'
                        : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-gray-50'
                    }`}
                  >
                    <User size={15} />
                    <span>{label}</span>
                  </Link>
                );
              })()}

              {/* "Test als..." dropdown — superadmin/owner only */}
              {canSwitch && (
                <div className="relative" ref={switchRef}>
                  <button
                    onClick={() => setSwitchOpen(!switchOpen)}
                    className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      user?.role === 'superadmin'
                        ? 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200'
                        : 'text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200'
                    }`}
                    title="Switch naar ander account voor testen"
                  >
                    <Shield size={14} />
                    <span>Test als…</span>
                    <ChevronDown size={12} className={switchOpen ? 'rotate-180' : ''} />
                  </button>

                  {switchOpen && (
                    <div className="absolute right-0 top-full mt-1.5 bg-white rounded-lg shadow-xl border border-gray-100 w-72 max-h-[70vh] overflow-y-auto z-50">
                      {/* Header — toon de huidige gebruiker prominent zodat
                           duidelijk is wie de "hoofd-account" is. De rest van
                           de lijst zijn accounts om naartoe te switchen. */}
                      <div className="px-3 py-2.5 bg-gradient-to-r from-blue-50 to-amber-50 border-b border-gray-100">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                          {user?.role === 'superadmin' ? 'Superadmin' : 'Hoofd-eigenaar'}
                        </p>
                        <p className="text-sm font-bold text-[#1a1a1a] mt-0.5">{user?.name ?? '—'}</p>
                        <p className="text-[10px] text-[#8a7e74]">{user?.email}</p>
                      </div>
                      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                        Schakel over naar
                      </div>
                      {switchAccounts.length === 0 ? (
                        <p className="px-3 py-4 text-xs text-gray-500">Laden…</p>
                      ) : (
                        // v3.2.63 — alle accounts blijven zichtbaar, current user
                        // krijgt een '(huidig)'-badge zodat Filip ziet dat hij na
                        // switching naar Bicek perfect kan terugschakelen naar
                        // zichzelf. Vroeger werd current user uitgefilterd, wat
                        // verwarring gaf.
                        switchAccounts.map(acc => {
                          const isCurrent = acc.email === user?.email;
                          const isSwitching = switching === acc.email;
                          // Wens Franky (19/8): toon bij handelaars meteen hun
                          // type (groothandel/kleinhandel/bouwonderneming/
                          // installateur) i.p.v. het generieke 'Reseller'.
                          const typeLabels: Record<string, string> = {
                            groothandel: 'Groothandel', kleinhandel: 'Kleinhandel',
                            bouwonderneming: 'Bouwonderneming', installateur: 'Installateur',
                          };
                          const roleLabel = acc.role === 'owner'    ? 'Eigenaar'
                                          : acc.role === 'reseller' ? ((typeLabels[acc.reseller_type ?? ''] ?? 'Reseller') + (acc.is_installateur && acc.reseller_type !== 'installateur' ? ' 🔨' : ''))
                                          : acc.role === 'customer' ? 'Klant'
                                          : acc.role;
                          const roleCls =
                            acc.role === 'owner' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                            acc.role === 'reseller' ? (
                              acc.reseller_type === 'groothandel'    ? 'bg-sky-100 text-sky-800 border-sky-200' :
                              acc.reseller_type === 'bouwonderneming'? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                              acc.reseller_type === 'installateur'   ? 'bg-violet-100 text-violet-800 border-violet-200' :
                              'bg-amber-100 text-amber-800 border-amber-200'
                            ) :
                            acc.role === 'customer' ? 'bg-green-100 text-green-800 border-green-200' :
                            'bg-gray-100 text-gray-600 border-gray-200';
                          return (
                            <button
                              key={acc.id}
                              disabled={isSwitching}
                              onClick={() => {
                                setSwitchOpen(false);
                                // Fix Franky (19/8): klik op het huidige account
                                // brengt je meteen naar zijn werkplek (vroeger
                                // gebeurde er niets en moest je apart op Admin
                                // klikken).
                                if (isCurrent) {
                                  window.location.href = acc.role === 'superadmin' || acc.role === 'owner' ? '/admin'
                                    : acc.role === 'reseller' ? '/reseller-dashboard' : '/dashboard';
                                  return;
                                }
                                void switchTo(acc);
                              }}
                              className={`w-full text-left px-3 py-2.5 transition-colors border-b border-gray-50 disabled:opacity-50 ${
                                isCurrent ? 'bg-emerald-50/50 hover:bg-emerald-50'
                                : acc.role === 'owner' ? 'bg-blue-50/40 hover:bg-blue-50'
                                : acc.role === 'reseller' ? (
                                    acc.reseller_type === 'groothandel'     ? 'bg-sky-50/60 hover:bg-sky-50'
                                    : acc.reseller_type === 'kleinhandel'   ? 'bg-amber-50/60 hover:bg-amber-50'
                                    : acc.reseller_type === 'bouwonderneming' ? 'bg-emerald-50/60 hover:bg-emerald-50'
                                    : acc.reseller_type === 'installateur'  ? 'bg-violet-50/60 hover:bg-violet-50'
                                    : 'bg-amber-50/40 hover:bg-amber-50')
                                : acc.role === 'customer' ? 'bg-green-50/30 hover:bg-green-50'
                                : 'hover:bg-[#faf7f3]'
                              }`}
                              title={isCurrent ? 'Je bent dit account — klik om naar je werkplek te gaan' : `Schakel over naar ${acc.name}`}
                            >
                              <div className="flex items-baseline gap-1.5">
                                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${roleCls}`}>
                                  {roleLabel}
                                </span>
                                <span className="text-[10px] text-[#8a7e74]">:</span>
                                <span className="text-xs font-semibold text-[#1a1a1a] truncate">{acc.name}</span>
                                {isCurrent && (
                                  <span className="ml-auto px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-[9px] font-bold uppercase tracking-wide">
                                    ✓ huidig
                                  </span>
                                )}
                                {isSwitching && <span className="text-[9px] text-[#c47a3a] ml-auto">…</span>}
                              </div>
                              <p className="text-[10px] text-[#8a7e74] truncate ml-[68px] mt-0.5">
                                {acc.reseller_company ?? acc.email}
                              </p>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* "Terug naar Admin" — visible while impersonating */}
              {!canSwitch && isImpersonating && (
                <button
                  onClick={goBackToAdmin}
                  className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors"
                  title="Terug naar admin"
                >
                  <Shield size={14} />
                  <span>Terug naar admin</span>
                </button>
              )}

              {/* 'Data DEMO'-link volledig verwijderd op vraag Franky (19/8);
                   de demo-omgeving blijft bereikbaar via /admin?demo=true. */}

              {/* Logout for logged-in users */}
              {user && (
                <button onClick={logout} className="hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-[#8a7e74] hover:text-[#1a1a1a] hover:bg-gray-50 transition-colors" title={t('nav.logout')}>
                  <LogOut size={15} />
                </button>
              )}

              {/* Inloggen button for guests */}
              {!user && (
                <button
                  onClick={() => setLoginOpen(true)}
                  className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-gray-50 border border-gray-200 hover:border-gray-300 transition-all"
                >
                  <User size={14} />
                  <span>Inloggen</span>
                </button>
              )}

              {/* Mobile hamburger */}
              <button
                onClick={() => setOpen(!open)}
                className="lg:hidden p-1.5 -mr-1 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors"
                aria-label="Menu"
                aria-expanded={open}
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '44px' }}
      >
        <div
          className="absolute inset-0 bg-black/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />

        <div
          ref={menuRef}
          className={`relative bg-white shadow-xl border-b border-gray-100 transition-all duration-300 ease-out ${
            open ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
          style={{ maxHeight: 'calc(100vh - 44px)', overflowY: 'auto' }}
        >
          {/* Navigation Links */}
          <div className="px-3 pt-3 pb-2">
            <div className="grid grid-cols-2 gap-1.5">
              {links.map(l => {
                const Icon = NAV_ICONS[l.to] || FileText;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm font-medium transition-all active:scale-[0.98] ${
                      location.pathname === l.to
                        ? 'text-[#c47a3a] bg-[#c47a3a]/8 shadow-sm'
                        : 'text-[#4a4a4a] hover:bg-gray-50 active:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{l.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Account section (mobile) */}
          {user ? (() => {
            const isAdmin = user.role === 'owner' || user.role === 'superadmin';
            const isReseller = user.role === 'reseller';
            const target = isAdmin ? '/admin' : isReseller ? '/reseller-dashboard' : '/dashboard';
            return (
              <div className="px-3 pb-3 border-t border-gray-100 pt-2">
                {/* Wens Franky (19/8): toon in het gsm-menu wie ingelogd is */}
                <div className="relative flex items-center gap-2 px-3 py-2 mb-1 bg-[#faf7f3] rounded-lg">
                  <span className="w-7 h-7 rounded-full bg-[#c47a3a] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {(user.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold text-[#1a1a1a] truncate">{user.name}</span>
                    <span className="block text-[10px] text-[#8a7e74] truncate">
                      Ingelogd · {user.role === 'owner' ? 'eigenaar' : user.role === 'superadmin' ? 'superadmin' : user.role === 'reseller' ? 'handelaar' : 'klant'} · {user.email}
                    </span>
                  </span>
                  {(canSwitch || isImpersonating) && (
                    <>
                      <span className="text-[#c47a3a] text-sm flex-shrink-0">wissel ⌄</span>
                      {/* Wens Franky (19/8): tik op de accountregel = meteen een
                           ander account kiezen (native keuzelijst eroverheen) */}
                      <select
                        value=""
                        onChange={e => {
                          const acc = switchAccounts.find(a => a.id === e.target.value);
                          if (!acc) return;
                          setOpen(false);
                          if (isImpersonating) { void wisselDirect(acc); } else { void switchTo(acc); }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0"
                        aria-label="Wissel naar een ander account"
                      >
                        <option value="">Wissel naar…</option>
                        {switchAccounts.filter(a => a.email !== user.email).map(a => (
                          <option key={a.id} value={a.id}>
                            {(a.role === 'owner' ? 'Eigenaar' : a.role === 'reseller' ? (a.reseller_type ?? 'reseller') : 'klant')} — {a.name.slice(0, 22)}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
                {/* Test-modus (Franky 19/8): op gsm duidelijk tonen dat je als
                     een ander account bezig bent + één tik terug naar admin */}
                {isImpersonating && (
                  <button
                    onClick={goBackToAdmin}
                    className="w-full mb-1 px-3 py-2 bg-amber-100 border border-amber-300 text-amber-900 rounded-lg text-xs font-bold text-left"
                  >
                    🛡 Test-modus — je echte account is {localStorage.getItem('gvs-admin-name') ?? 'admin'}. Tik om terug te keren.
                  </button>
                )}
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <Link
                    to={target}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-[#c47a3a] rounded-lg hover:bg-[#c47a3a]/5 min-h-[44px]"
                  >
                    {isAdmin ? <Settings size={16} /> : <User size={16} />}
                    {isAdmin ? t('nav.dashboard') : 'Mijn account'}
                  </Link>
                  <button
                    onClick={() => { logout(); setOpen(false); }}
                    className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-[#8a7e74] rounded-lg hover:bg-gray-50 min-h-[44px]"
                  >
                    <LogOut size={14} />
                    {t('nav.logout')}
                  </button>
                </div>
                {/* Wens Franky (19/8): 'Test als…' ook op de gsm — als eigenaar
                     schakel je hier meteen naar eender welk account. */}
                              </div>
            );
          })() : (
            <div className="px-3 pb-3 border-t border-gray-100 pt-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setOpen(false); setLoginOpen(true); }}
                  className="flex items-center gap-2 flex-1 px-3 py-2.5 text-sm font-medium text-[#4a4a4a] rounded-lg hover:bg-gray-50"
                >
                  <User size={15} />
                  Inloggen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}
